import type { HistoricalData } from '@/queries/historical.query'
import { formatDate } from '@vaultsfyi/common/utils'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Loader } from '../common/loader'
import { ChartBrush, SingleAreaBrushPanorama } from './chartBrush'
import { ChartContainer } from './chartContainer'
import { ChartTooltip } from './chartTooltip'
import { ChartWatermark } from './chartWatermark'

interface AreaChartProps {
  data: HistoricalData
  className?: string
  showBrush?: boolean
}

export const SharePriceAreaChart = ({ data, className, showBrush = false }: AreaChartProps) => {
  if (data.length === 0) {
    return (
      <ChartContainer className={className}>
        <Loader />
      </ChartContainer>
    )
  }

  return (
    <ChartContainer className={className}>
      <ResponsiveContainer>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="sharePriceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-yellow))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-yellow))" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="timestamp"
            tickLine={false}
            tickMargin={12}
            type="number"
            interval={'preserveStartEnd'}
            domain={['dataMin', 'dataMax']}
            tickCount={7}
            tickFormatter={(timestamp) => formatDate({ timestamp })}
          />
          <YAxis
            width={56}
            tickMargin={0}
            tickLine={false}
            tickFormatter={(value: number) => value.toFixed(4)}
            interval="preserveStartEnd"
            domain={['auto', 'auto']}
          />
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
          <Tooltip content={({ payload }) => <SharePriceChartTooltip data={payload?.[0]?.payload} />} />
          <Area
            dataKey="sharePrice"
            type="monotone"
            isAnimationActive={false}
            stroke="rgb(var(--chart-yellow))"
            strokeOpacity={1}
            strokeWidth={2}
            fill="url(#sharePriceFill)"
          />
          <ChartWatermark />
          {showBrush && (
            <ChartBrush data={data}>
              <SingleAreaBrushPanorama
                color="rgb(var(--chart-yellow))"
                dataKey="sharePrice"
                gradientId="sharePriceBrushFill"
              />
            </ChartBrush>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

const SharePriceChartTooltip = ({ data }: { data: HistoricalData[number] | undefined }) => {
  if (!data) return null
  return (
    <ChartTooltip>
      <span>{formatDate({ timestamp: data.timestamp, showYear: true })}</span>
      <p className="font-semibold">Price: {data.sharePrice.toFixed(5)}</p>
    </ChartTooltip>
  )
}
